import styled from "@emotion/styled";
import SensorIcon from "components/gateways/SensorIcon";
import { useEffect, useRef, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
    buildSparklineSeriesWithMissingGaps,
    CardSensorReading,
    getCardMetric,
    getCardSensorReadings,
} from "service/proqueryService";
import { DeviceInfo } from "types";
import { UnknownDevice } from "constant";
import {
    IconCircle,
    ReadingTimeText,
    SensorContent,
    SensorLabel,
    SensorType,
} from ".";
import { SparklineChart } from "./components/SparklineChart";
import { isBinarySensor } from "utils/functions";
import {
    getCurrentValueToDisplay,
    getReadingDateTimeToDisplay,
} from "utils/gatewayFunctions";

import { CardMetric } from "./MultiSensor";
import ErrorOutlineOutlined from "@mui/icons-material/ErrorOutlineOutlined";

const CardContent = styled.div`
    display: flex;
    align-items: center;
    width: 100%;
`;

const SensorInfo = styled.div`
    display: flex;
    flex-direction: column;
    width: calc(100% - 35px);
`;

const Value = styled.div<{ fontSize?: number | string }>`
    font-weight: 600;
    font-style: SemiBold;
    font-size: ${({ fontSize }) => fontSize ?? "18"}px;
    line-height: 100%;
    letter-spacing: 0%;
    text-align: right;
    color: #00cbaf;
    width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

interface SensorCardProps {
    devices: DeviceInfo[];
    panel: any;
    widgetId: string;
}

const LDSUReading = ({ devices, widgetId, panel }: SensorCardProps) => {
    const [dataCharts, setDataCharts] = useState<CardMetric[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);
    const isMountedRef = useRef(true);
    const [readings, setReadings] = useState<CardSensorReading[]>([]);

    const refreshRate = 30 * 1000;
    const defaultAccuracy = 0;

    useEffect(() => {
        isMountedRef.current = true;

        const fetchChartData = async () => {
            const { queries, attributes } = panel;
            if (!attributes?.[0] || !devices.length) {
                return;
            }

            try {
                const gatewayId = attributes[0].gateway_id;
                const deviceId = attributes[0].device_id;

                const [chartMetric, newReadings] = await Promise.all([
                    getCardMetric({
                        queries,
                    }),
                    getCardSensorReadings({ gatewayId, deviceId, devices }),
                ]);

                if (!isMountedRef.current) return;
                setReadings(newReadings ?? []);
                setDataCharts(chartMetric as CardMetric[]);
            } catch (err) {
                console.error(err);
            }
        };

        fetchChartData();
        const fetchMetricInterval = setInterval(fetchChartData, refreshRate);

        return () => {
            isMountedRef.current = false;
            clearInterval(fetchMetricInterval);
        };
    }, [devices, refreshRate, panel]);

    const getReadingValue = (
        said: number,
        accuracy: number | null,
        unit: string
    ) => {
        const readingData = readings.find(
            (d) => Number(d.said) === Number(said)
        );

        if (
            readingData?.value === null ||
            readingData?.value === undefined ||
            readingData?.lastActive === null ||
            readingData?.lastActive === undefined
        ) {
            return "-";
        }

        return getCurrentValueToDisplay(
            readingData.value,
            readingData.lastActive,
            Number(accuracy ?? defaultAccuracy),
            unit
        );
    };

    const getReadingDateTime = (said: number) => {
        const readingData = readings.find(
            (d) => Number(d.said) === Number(said)
        );

        return getReadingDateTimeToDisplay(readingData?.lastActive ?? null);
    };

    const renderSensor = () => (
        <div className="d-flex flex-column flex-fill">
            {devices.map(
                ({
                    sensorType,
                    deviceId,
                    said,
                    sensorName,
                    accuracy,
                    chartColor,
                    unit,
                    gatewayId,
                    cls,
                    format,
                }) => {
                    const isBinary = isBinarySensor(cls, format);
                    const chartPoints =
                        dataCharts.find(
                            (d) =>
                                d.said === Number(said) &&
                                d.gatewayId === gatewayId &&
                                d.ldsuUId === deviceId
                        )?.value ?? [];
                    const sparklineSeries = sensorName
                        ? buildSparklineSeriesWithMissingGaps({
                              points: chartPoints,
                              color: chartColor,
                              isBinaryChart: isBinary,
                              accuracy: accuracy ?? defaultAccuracy,
                          })
                        : [];

                    return (
                        <Row
                            className="mb-3 align-items-center"
                            key={`${widgetId}-${deviceId}-${said}`}
                        >
                            <Col xs={4} md={5} lg={4}>
                                <SensorContent>
                                    <IconCircle size={35} className="mr-2">
                                        {sensorName ? (
                                            <SensorIcon
                                                sensorType={sensorType}
                                                cls={cls}
                                            />
                                        ) : (
                                            <ErrorOutlineOutlined
                                                color="error"
                                                fontSize="large"
                                                data-testid="sensor-name-missing-error-icon"
                                            />
                                        )}
                                    </IconCircle>
                                    <SensorInfo>
                                        <SensorLabel
                                            fontSize={16}
                                            className="text-truncate"
                                        >
                                            {sensorName ??
                                                sensorType ??
                                                UnknownDevice}
                                        </SensorLabel>
                                        <SensorType
                                            fontSize={14}
                                            className="text-truncate"
                                        >
                                            {sensorType}
                                        </SensorType>
                                    </SensorInfo>
                                </SensorContent>
                            </Col>
                            <Col xs={3} md={3} lg={4}>
                                {sensorName && sparklineSeries.length > 0 && (
                                    <SparklineChart
                                        gapSplitSeries={sparklineSeries}
                                        color={chartColor}
                                    />
                                )}
                            </Col>
                            <Col xs={5} md={4} lg={4}>
                                <div
                                    className="d-flex flex-column align-items-end text-end"
                                    style={{ minWidth: 0 }}
                                >
                                    <Value>
                                        {getReadingValue(said, accuracy, unit)}
                                    </Value>
                                    <ReadingTimeText
                                        fontSize={14}
                                        className="text-truncate mt-1"
                                        style={{ maxWidth: "100%" }}
                                    >
                                        {getReadingDateTime(said)}
                                    </ReadingTimeText>
                                </div>
                            </Col>
                        </Row>
                    );
                }
            )}
        </div>
    );

    return (
        <CardContent ref={containerRef} className="mt-2 mt-sm-0">
            {renderSensor()}
        </CardContent>
    );
};

export default LDSUReading;
